import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import proptypes from 'prop-types';
const { string, bool, func } = proptypes;

import {
  extend,
  params,
  number as numberType,
  bool as boolType,
  string as stringType,
  shape as shapeType,
  arrayOf as arrayType,
  useDataModel, arrayOf
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';
import { Button } from '@thd-olt-component-react/core-ui';
import { StoreSearch } from '@thd-olt-component-react/store-search';

import { useRentalInventoryApi } from '../hooks/useRentalInventoryApi';
import { CheckNearbyStores } from './CheckNearbyStores/CheckNearbyStores';
import { StoreCard } from './StoreCard/StoreCard';
import { LoadingFulfillment } from './LoadingFulfillment/LoadingFulfillment';
import { WarningMessage } from './WarningMessage/WarningMessage';
import { LARGE_EQUIP_CATEGORIES } from '../utils/constants';

import styles from './rental-fulfillment.module.scss';

export const RentalFulfillment = ({
  itemId,
  handleButtonClick,
  showReserveNowButton,
  deliveryOnly,
  handleDeliveryButtonClick,
  graphQlEnabled
}) => {
  const [opened, toggleOpened] = useState(false);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('rental-fulfillment.ready');
  }, []);

  const { data } = useDataModel('product', { variables: { itemId } });
  const { storeId } = useStore();

  const [searchInput, setSearchInput] = useState(null);
  const [searchCoordinates, setSearchCoordinates] = useState(null);

  useEffect(() => setSearchInput(storeId), [storeId]);

  const options = {
    ssr: !!storeId,
    variables: {
      storeSearchInput: storeId,
      pagesize: 1
    },
  };

  const { data: storeSearchData } = useDataModel('storeSearch', options);
  const storeList = storeSearchData?.storeSearch?.stores;

  const {
    brandName,
    productLabel,
    rentalCategory,
    rentalSubCategory
  } = data?.product?.identifiers || {};
  const imageUrl = data?.product?.media?.image?.url;

  const isLargeEquipment = () => LARGE_EQUIP_CATEGORIES.includes(rentalCategory);

  const { data: stores, loading: isLoading, error: isError } = useRentalInventoryApi({
    rentalCategory,
    rentalSubCategory,
    storeList,
    graphQlEnabled
  });

  const handleToggle = () => {
    toggleOpened(true);
  };

  const LoadNearbyStoresModal = () => {
    const localizedStore = stores?.find((ls) => ls.storeId === storeId.padStart(4, '0'));
    return (
      opened && (
        <div className={classNames(styles['rental-fulfillment__options'])}>
          <StoreSearch
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            searchCoordinates={searchCoordinates}
            setSearchCoordinates={setSearchCoordinates}
          >
            <CheckNearbyStores
              brandName={brandName}
              clientStore={localizedStore}
              imageUrl={imageUrl}
              opened={opened}
              productLabel={productLabel}
              rentalCategory={rentalCategory}
              rentalSubCategory={rentalSubCategory}
              searchInput={searchInput}
              toggleOpened={toggleOpened}
              graphQlEnabled={graphQlEnabled}
            />
          </StoreSearch>
        </div>
      )
    );
  };

  const showReserveOption = () => {
    if (stores && stores?.length !== 0 && !isLoading && !isError) {
      const localizedStore = stores.find((ls) => ls.storeId === storeId.padStart(4, '0'));
      return (
        <div className={classNames(styles['rental-fulfillment__details-container'])}>
          <StoreCard
            store={localizedStore}
          />
          <div className={classNames(styles['rental-fulfillment__button-container'])}>
            <Button
              onClick={handleButtonClick}
            >
              Reserve for Store Pickup
            </Button>
          </div>
        </div>
      );
    }
    return (
      <LoadingFulfillment />
    );
  };

  const showHowToOption = () => {
    if (stores && stores?.length !== 0 && !isLoading && !isError) {
      const localizedStore = stores.find((ls) => ls.storeId === storeId.padStart(4, '0'));
      return (
        <div className={classNames(styles['rental-fulfillment__how-to-container'])}>
          <StoreCard
            store={localizedStore}
          />
          <div className={classNames(styles['rental-fulfillment__button-container'])}>
            <WarningMessage />
          </div>
        </div>
      );
    }
    return (
      <LoadingFulfillment />
    );
  };

  const showDeliveryOption = () => {
    if (stores && stores?.length !== 0 && !isLoading && !isError) {
      return (
        <div className={classNames(styles['rental-fulfillment__details-container'])}>
          <div className={classNames(styles['rental-fulfillment__location'])}>
            <div className="u--paddingBottom">
              Click <b>&quot;Schedule a Delivery&quot;</b> to book your next large equipment delivery.
            </div>
            <div className="u--paddingBottom">
              Delivery and pickup at your jobsite requires a minimum 1-day rental.
            </div>
          </div>
          <div className={classNames(styles['rental-fulfillment__button-container'])}>
            <div style={{ padding: '0 5px' }}>
              &nbsp;
            </div>
            <Button
              onClick={handleDeliveryButtonClick}
              outline={!!showReserveNowButton}
            >
              Schedule A Delivery
            </Button>
          </div>
        </div>
      );
    }
    return (
      <LoadingFulfillment />
    );
  };

  return (
    <div className="rental-fulfillment-container">
      {!deliveryOnly && (
        <div className={classNames(styles['rental-fulfillment-container__check-nearby-stores'])}>
          <LoadNearbyStoresModal />
          <span>
            {/* eslint-disable-next-line */}
            <a className="u__default-link check__nearstore--wrapper"
              role="link"
              data-testid="checkNearbyStores"
              onClick={handleToggle}
            >
              Check Nearby Stores for Immediate Pickup
            </a>
          </span>
        </div>
      )}
      {showReserveNowButton && !deliveryOnly && (
        <div className={classNames(styles['rental-fulfillment'])} data-component="RentalFulfillment">
          <div className={classNames(styles['rental-fulfillment__title'])}>
            <span>&nbsp;Reserve&nbsp;</span>
          </div>
          {showReserveOption()}
        </div>
      )}
      {!showReserveNowButton && !deliveryOnly && (
        <div className={classNames(styles['rental-fulfillment'])} data-component="RentalFulfillment">
          <div className={classNames(styles['rental-fulfillment__title'])}>
            <span>&nbsp;How to Get It&nbsp;</span>
          </div>
          {showHowToOption()}
        </div>
      )}
      {isLargeEquipment()
        && (
          <div className="rental-fulfillment" data-component="RentalFulfillment">
            <div className={classNames(styles['rental-fulfillment__title'])}>
              <span>&nbsp;Delivery&nbsp;</span>
            </div>
            {showDeliveryOption()}
          </div>
        )}
    </div>
  );
};

RentalFulfillment.displayName = 'RentalFulfillment';

RentalFulfillment.propTypes = {
  handleButtonClick: func,
  itemId: string.isRequired,
  deliveryOnly: bool,
  showReserveNowButton: bool,
  graphQlEnabled: bool,
  handleDeliveryButtonClick: func
};

RentalFulfillment.defaultProps = {
  showReserveNowButton: true,
  handleButtonClick: () => {},
  deliveryOnly: false,
  handleDeliveryButtonClick: () => { window.location.href = 'https://www.compactpowerrents.com/onsite-delivery/'; },
  graphQlEnabled: false
};

RentalFulfillment.dataModel = extend(
  StoreSearch,
  {
    product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
      dataSources: stringType(),
      itemId: stringType(),
      identifiers: shapeType({
        brandName: stringType(),
        productLabel: stringType(),
        rentalCategory: stringType(),
        rentalSubCategory: stringType()
      }),
      media: shapeType({
        image: shapeType({
          url: stringType()
        }).client(),
        images: arrayType(shapeType({
          sizes: arrayType(stringType()),
          subType: stringType(),
          type: stringType(),
          url: stringType()
        }))
      }),
    }),
    storeSearch: params({ storeSearchInput: stringType().isRequired(), pagesize: stringType() }).shape({
      stores: arrayType(shapeType({
        storeId: stringType(),
        name: stringType(),
        phone: stringType(),
        toolRentalPhone: stringType(),
        distance: stringType(),
        address: shapeType(
          {
            city: stringType(),
            country: stringType(),
            county: stringType(),
            postalCode: stringType(),
            state: stringType(),
            street: stringType()
          }
        ),
        services: shapeType({
          toolRental: boolType()
        })
      }))
    }),
    getRentalOnHands: params({
      storeList: arrayOf(stringType()),
      categoryCode: stringType(),
      subCategoryCode: stringType()
    }).arrayOf(shapeType({
      availableQty: stringType(),
      categoryCd: stringType(),
      description: stringType(),
      locationNbr: stringType(),
      onHoldQty: numberType(),
      onOrderQty: numberType(),
      outForRepairQty: numberType(),
      outOnContractQty: numberType(),
      subCategoryCd: stringType()
    }))
  }
);
